import api, { dataOf } from './client'

export interface TeamRow {
  id: string
  name: string
  status: 'active' | 'suspended' | 'dissolving' | 'dissolution_failed' | 'dissolved' | 'legacy'
  task_restricted: boolean
  owner_id: string
  owner_name: string
  join_requires_approval: boolean
  member_count: number
  project_count: number
  canvas_count: number
  generated_tasks: number
  terminal_tasks: number
  successful_tasks: number
  image_outputs: number
  video_outputs: number
  created_at: string
  available_points?: number
  reserved_points?: number
  paid_points?: number
  absorbed_points?: number
  supplier_cost_nanos?: number
  recharged_points?: number
  task_refunded_points?: number
  payment_refund_fen?: number
}

export interface TeamPage {
  items: TeamRow[]
  total: number
  plans: Array<{ id: string; name: string }>
  summary: {
    active_teams: number
    active_members: number
    projects: number
    canvases: number
    generated_tasks: number
    terminal_tasks: number
    successful_tasks: number
    image_outputs: number
    video_outputs: number
    finance?: {
      task_count: number
      due_points: number
      paid_points: number
      absorbed_points: number
      supplier_cost_nanos: number
      recharged_points: number
      recharge_amount_fen: number
      low_balance_teams: number
      unsettled_tasks: number
      unsettled_points: number
      missing_cost_tasks: number
      pending_usage_calls: number
      paid_uncredited_orders: number
      task_refunded_points: number
      payment_refund_amount_fen: number
      current_reserved_points: number
      negative_available_teams: number
      over_estimate_tasks: number
      long_held_tasks: number
      over_budget_projects: number
      duplicate_callbacks: number
      legacy_manual_credits: number
    }
  }
  page: number
  page_size: number
  from: string
  to: string
  can_view_finance: boolean
}

export interface TeamLimits {
  max_created_teams: number
  max_joined_teams: number
  revision: number
  updated_at: string
}

export interface TeamHealth {
  id: string
  status: TeamRow['status']
  active_media_jobs: number
  active_agent_runs: number
  from: string
  to: string
  can_view_finance: boolean
  available_points?: number
  expiring_soon_points?: number
  reserved_points?: number
  pending_model_calls?: number
  provider_review_jobs?: number
  unsettled_tasks?: number
  unsettled_points?: number
  pending_orders?: number
  pending_refunds?: number
  pending_invoices?: number
  missing_cost_tasks?: number
  paid_uncredited_orders?: number
  negative_available?: boolean
  long_held_tasks?: number
  over_estimate_tasks?: number
  over_budget_projects?: number
  duplicate_callbacks?: number
  legacy_manual_credits?: number
}

export interface TeamUsageItem {
  task_id: string
  user_id: string
  member_name: string
  source_type: string
  project_id: string
  models: string
  providers: string
  execution_status: string
  settlement_status: 'unsettled' | 'absorbed' | 'settled' | 'no_charge'
  pricing_rate: string
  risk_flags: string
  due_points: number
  paid_points: number
  absorbed_points: number
  unsettled_points: number
  supplier_cost_nanos: number
  estimated_points: number | null
  admitted_reserved_points: number | null
  ledger_ids: string
  idempotency_keys: string
  created_at: string
  updated_at: string
}

export interface TeamUsagePage {
  team_id: string
  from: string
  to: string
  page: number
  page_size: number
  total: number
  summary: {
    task_count: number
    due_points: number
    paid_points: number
    absorbed_points: number
    unsettled_points: number
    supplier_cost_nanos: number
  }
  items: TeamUsageItem[]
}

export interface TeamUsageFilters {
  member_id: string
  project_id: string
  model: string
  status: string
}

export interface TeamRiskCase {
  id: number
  workspace_id: string
  kind: string
  source_id: string
  status: 'open' | 'acknowledged' | 'processing' | 'pending_review' | 'closed'
  source_active: boolean
  detected_at: string
  last_seen_at: string
  reason: string
  handled_by?: number
  reviewed_by?: number
  closed_at?: string
  assigned_admin_id?: number
  assigned_admin_name?: string
  due_at?: string
  evidence_reference?: string
  revision: number
}

export interface TeamRiskEvent {
  actor_id: number
  action: string
  reason: string
  old_status: string
  new_status: string
  created_at: string
}

export const teamsApi = {
  limits: () => dataOf<TeamLimits>(api.get('/teams/limits')),
  updateLimits: (input: Pick<TeamLimits, 'max_created_teams' | 'max_joined_teams' | 'revision'> & { reason: string }) =>
    dataOf<TeamLimits>(api.patch('/teams/limits', input)),
  list: (page: number, search: string, status: string, planId: string, from: string, to: string) =>
    dataOf<TeamPage>(api.get('/teams', { params: { page, page_size: 20, search, status, plan_id: planId, from, to } })),
  health: (id: string, from: string, to: string) =>
    dataOf<TeamHealth>(api.get(`/teams/${encodeURIComponent(id)}/health`, { params: { from, to } })),
  risks: (id: string) => dataOf<{ items: TeamRiskCase[]; assignees?: Array<{id: number; username: string; role: string}> }>(api.get(`/teams/${encodeURIComponent(id)}/risks`)),
  followUpRisk: (id: string, caseId: number, input: { revision: number; assigned_admin_id: number; due_at: string; evidence_reference: string; reason: string }) =>
    dataOf<{ revision: number }>(api.patch(`/teams/${encodeURIComponent(id)}/risks/${caseId}/follow-up`, input)),
  scanRisks: (id: string) => dataOf<{ active: number; scanned_at: string }>(api.post(`/teams/${encodeURIComponent(id)}/risks/scan`)),
  riskEvents: (id: string, caseId: number) => dataOf<{ items: TeamRiskEvent[] }>(api.get(`/teams/${encodeURIComponent(id)}/risks/${caseId}/events`)),
  transitionRisk: (id: string, caseId: number, action: string, reason: string, revision: number) =>
    dataOf<{ status: TeamRiskCase['status']; revision: number }>(api.patch(`/teams/${encodeURIComponent(id)}/risks/${caseId}`, { action, reason, revision })),
  usage: (id: string, from: string, to: string, page: number, filters: TeamUsageFilters) =>
    dataOf<TeamUsagePage>(api.get(`/teams/${encodeURIComponent(id)}/usage`, {
      params: { from, to, page, page_size: 20, ...filters },
    })),
  setTaskRestriction: (id: string, restricted: boolean, reason: string) =>
    dataOf<{ restricted: boolean }>(api.patch(`/teams/${encodeURIComponent(id)}/task-restriction`, { restricted, reason })),
  setSuspension: (id: string, suspended: boolean, reason: string) =>
    dataOf<{ suspended: boolean }>(api.patch(`/teams/${encodeURIComponent(id)}/suspension`, { suspended, reason })),
  export: async (search: string, status: string, planId: string, from: string, to: string) => {
    const result = await api.get<Blob>('/teams/export', { params: { search, status, plan_id: planId, from, to }, responseType: 'blob' })
    const url = URL.createObjectURL(result.data)
    try {
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = '团队目录.csv'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
  },
}
